// @ts-strict-ignore
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  QueryList,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { LMFormPanelBase } from './lm-form-panel-base';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMListComponent, LMListTransactionEvent } from '@list/lm-list.component';
import { forkJoin, Observable, Observer } from 'rxjs';
import { LMPanelTransactionEvent } from '@form/services/lm-form-panel.service';
import { LMUrlInfoBuilderService } from '@services/lm-url-info-builder.service';
import { LMRefreshRequest } from '@app/components';
import { LMMultiListSaveWorker } from '@app/utils';

@Component({
  selector: 'lm-form-panel-multilist',
  templateUrl: 'lm-form-panel-multilist.component.html',
  styleUrls:   [ 'lm-form-panel-multilist.component.scss' ]
})

export class LMFormPanelMultiListComponent implements LMFormPanelBase {
  @ViewChildren('LMListComponent') listComponents: QueryList<LMListComponent>;

  @HostBinding('style.height') get getHeight(): string {
    if (this.hasPanelText) {
      return 'auto';
    } else {
      return '100%';
    }
  }

  @Input() placeHolder: ViewContainerRef;
  @Input() needsReload: boolean;
  @Input() panelDataState: LMPanelDataState;
  @Input() activated: boolean;
  @Input() sectionSpecs?: Array<LMMultiListSectionSpec>;
  @Input() panelId: string;
  @Input() hasPanelText = false;
  @Input() specFields: LMSpecFields;

  @Input() set fromDataView(fromDataView: LMDataView) {
    this._fromDataView = fromDataView;
    this._numberVisibleLists = 0;
    this.listViewData = this.sectionSpecs.map(list => this.toListViewData(list));
  }

  get fromDataView(): LMDataView {
    if (this.needsReload || (this.panelDataState === 'stale_needsReload')) {
      // ------------------------------------------------------------
      // if needs reload then just send a null for the fromDataView
      // null will cause the list to unload it's data but not ask
      // for new data. Once needsReload is set back to false then
      // the target content will load.
      // ------------------------------------------------------------
      return null;
    }

    return this._fromDataView;
  }

  @Output() contentModified = new EventEmitter<any>();
  @Output() formPanelContentLoaded = new EventEmitter<any>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() specialActionToolbarButtonEvent = new EventEmitter<LMSpecialActionToolbarButtonEvent>();
  @Output() panelTransactionEvent = new EventEmitter<LMPanelTransactionEvent>();
  @Output() drillAroundEvent = new EventEmitter<LMDrillAroundEvent>();

  private _fromDataView: LMDataView;
  public listViewData: Array<LMListViewData>;

  private _numberVisibleLists = 0;

  constructor(
    private lmFormatUtilService: LMFormatUtilService,
    private lmConditionUtilService: LMConditionUtilService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private elementRef: ElementRef
  ) { }

  public getPanelHeight(): number {
    if (this.elementRef) {
      return this.elementRef.nativeElement.offsetHeight;
    }
    return 0;
  }

  public get numberVisibleLists(): number {
    return this._numberVisibleLists;
  }

  private toListViewData(section: LMMultiListSectionSpec): LMListViewData {
    if (!section.hasOwnProperty('listSpec')) {
      return null;
    }

    const listViewData: LMListViewData = {
      listUrlInfo: this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(section.listSpec),
      listSpec: section.listSpec
    };

    if (section.showLines) {
      // kludge for now. setting the busListSpec for this multipane list
      // to the multipane lists showLines. In this way I can communicate
      // the multipane lists number of recs to show through the list spec.
      listViewData.listSpec.busListSpec.showLines = section.showLines;
    }

    if (section.panelText) {
      listViewData.panelText = section.panelText;
    }

    listViewData.isVisible = (section.visibility)
      ? this.lmConditionUtilService.isValid([ section.visibility ], this._fromDataView)
      : true;

    if (listViewData.isVisible) {
      this._numberVisibleLists++;
    }

    if (section.specialActions) {
      listViewData.specialActions = section.specialActions;
    }

    return listViewData;
  }

  onContentModified(event: any) {
    this.contentModified.emit(event);
  }

  onListDisplayed(event: any) {
    this.formPanelContentLoaded.emit(event);
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onSpecialActionToolbarButtonEvent(event: LMSpecialActionToolbarButtonEvent) {
    this.specialActionToolbarButtonEvent.emit(event);
  }

  onPanelTransactionEvent(event: LMListTransactionEvent) {
    const originatingId = this.panelId;
    const originatingList = event.eventSource.listId;
    const panelTransactionEvt = new LMPanelTransactionEvent(originatingId);
    panelTransactionEvt.originatingList = originatingList;
    this.panelTransactionEvent.emit(panelTransactionEvt);
  }

  onDrillAroundEvent(event: LMDrillAroundEvent) {
    this.drillAroundEvent.emit(event);
  }

  public refresh(request?: LMRefreshRequest): Observable<boolean> {
    const lists = this.listComponents ? this.listComponents.toArray() : [];
    return new Observable<boolean>(observer => {
      const refreshCalls: Observable<boolean>[] = lists.filter(list => list.listId !== request?.originatingSourceId)
        .map((list: LMListComponent) => list.refresh());

      // console.log('multilist to be refresh:' + refreshCalls.length);

      forkJoin(refreshCalls).subscribe(value => {
        // console.log('multilist to be forkJoin:');
        observer.next(true);
        observer.complete();
      });
    });
  }

  public save(callBack: Observer<boolean>) {
    this.runSaveList().subscribe(
      (continueOn: boolean) => {
        callBack.next(continueOn);
        callBack.complete();
      });
  }

  trackByListSpec(index: number, list: LMListViewData): LMListSpec {
    return list.listSpec;
  }

  private runSaveList(): Observable<boolean> {
    const listComponents: LMListComponent[] = this.listComponents.toArray();
    const saveWorker: LMMultiListSaveWorker = new LMMultiListSaveWorker(listComponents);
    return saveWorker.start();
  }

  public isDirty(): boolean {
    const listComponents: LMListComponent[] = this.listComponents.toArray();

    for (let i = 0, l = listComponents.length; i < l; i++) {
      if (listComponents[ i ].isModified()) {
        return true;
      }
    }
    return false;
  }

  saveCompositeComponentState(): void {
    this.listComponents.toArray().forEach(listComponent => listComponent.saveComponentState(true));
  }

  clearCompositeComponentState(): void {
    this.listComponents.toArray().forEach(listComponent => listComponent.clearComponentState(true));
  }
}
